import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import QuestionImage from "./components/QuestionImage";
import { db } from "./firebaseConfig";
import "./ViewQuizResponses.css";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  documentId,
  writeBatch,
  runTransaction,
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Download,
  FileQuestion,
  KeyRound,
  ListChecks,
  Search,
  Sparkles,
  Trophy,
  UsersRound,
  X,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import McqResultSummary from "./components/McqResultSummary";
import SubmittedDrawingAnswer from "./components/SubmittedDrawingAnswer";
import AnswerKeyCorrectionDialog from "./components/AnswerKeyCorrectionDialog";
import AiMarkingProgressPanel from "./components/AiMarkingProgressPanel";
import {
  attemptLabel,
  attemptProgression,
  buildAssessmentSeries,
  finalPolicyLabel,
  outcomeDisplayLabel,
  questionNumberLabel,
  quizWideSubmissionRecords,
  selectDetailedAttemptId,
  selectLatestAttemptRecords,
  submissionNeedsMarking,
  unmarkedSubmissionRecords,
} from "./utils/attemptResults";
import {
  assessmentResultsCsvFilename,
  buildAssessmentResultsCsv,
} from "./utils/assessmentResultsCsv";
import { API_BASE_URL } from "./utils/apiBase";
import { WorkTypeFilter, WorkTypeTag } from "./components/WorkType";
import { matchesWorkType, withSeriesWorkTypes } from "./utils/workType";

const normalizeUserRole = (role) => String(role || "").trim().toLowerCase();

// Summarises a marking run from its final outcomes, after every retry and
// re-check pass, counting each answer once. `returnedOutcomes` is in the order
// the marking API returned them, so an answer's last outcome is its final one
// and a later success clears an earlier marking error. An answer is marked only
// if its final outcome has no `markingError`; it requires attention if no pass
// returned an outcome for it or its final outcome is a marking error (including
// one where the server kept an earlier mark).
export const summarizeMarkingRun = (submissions, returnedOutcomes = []) => {
  const ids = [...new Set(submissions.map(({ id }) => id))];
  const finalOutcomeById = new Map(returnedOutcomes.filter((outcome) => outcome?.id).map((outcome) => [outcome.id, outcome]));
  const marked = ids.filter((id) => finalOutcomeById.has(id) && !finalOutcomeById.get(id).markingError).length;
  return {
    processed: ids.length,
    marked,
    requiringAttention: ids.length - marked,
    hasFailures: marked < ids.length,
  };
};

function ChooseOwnedTestDialog({ userUid, onClose, onSelect }) {
  const [tests, setTests] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const selectingRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const load = async () => {
      try {
        const snapshot = await getDocs(query(collection(db, "quizzes"), where("creatorUid", "==", userUid)));
        if (!active) return;
        setTests(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((test) => test.creatorUid === userUid && typeof test.quizCode === "string" && test.quizCode.trim())
          .sort((a, b) => (a.quizTitle || a.title || a.quizCode).localeCompare(b.quizTitle || b.title || b.quizCode)));
      } catch (loadError) {
        console.error("Error loading teacher-owned quizzes:", loadError);
        if (active) setError("Could not load your tests. Try again.");
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [userUid, retryKey]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const filtered = tests.filter((test) =>
    `${test.quizTitle || test.title || ""} ${test.subject || ""} ${test.quizCode}`
      .toLowerCase().includes(search.trim().toLowerCase()));
  const select = (test) => {
    if (selectingRef.current) return;
    selectingRef.current = true;
    onSelect(test.quizCode);
  };

  return <div className="vqr-modal-overlay vqr-test-picker-overlay" onMouseDown={(event) => {
    if (event.target === event.currentTarget) onClose();
  }}>
    <div className="vqr-modal vqr-test-picker" role="dialog" aria-modal="true" aria-labelledby="vqr-test-picker-title">
      <button type="button" className="vqr-modal-close" onClick={onClose} aria-label="Close test chooser"><X size={19} /></button>
      <span className="vqr-icon-tile vqr-tone-purple"><BookOpen size={21} /></span>
      <h2 id="vqr-test-picker-title">Choose a test</h2>
      <p className="vqr-modal-description">Select one of your tests to load its student responses.</p>
      {error && <div className="vqr-test-picker-error" role="alert"><span>{error}</span>
        <button type="button" onClick={() => setRetryKey((value) => value + 1)}>Retry</button></div>}
      {!loading && !error && <label className="vqr-test-picker-search"><Search size={17} />
        <input type="search" aria-label="Search my tests" placeholder="Search my tests…" value={search}
          onChange={(event) => setSearch(event.target.value)} autoFocus /></label>}
      {loading ? <p className="vqr-test-picker-state" role="status">Loading your tests…</p> : error ? null :
        filtered.length ? <div className="vqr-test-picker-list">{filtered.map((test) =>
          <div className="vqr-test-picker-row" key={test.id}>
            <div><strong>{test.quizTitle || test.title || test.quizCode}</strong>
              <small>{test.subject || "Uncategorized"} · Test code {test.quizCode}
                {Array.isArray(test.questionIds) && ` · ${test.questionIds.length} questions`}</small></div>
            <button type="button" onClick={() => select(test)}>Load Responses</button>
          </div>
        )}</div> : <p className="vqr-test-picker-state">{tests.length ? "No matching tests." : "No tests with a code are available in My Tests yet."}</p>}
    </div>
  </div>;
}

const ViewQuizResponses = ({ user }) => {
  const deepLink = useMemo(() => new URLSearchParams(window.location.search), []);
  const [quizCode, setQuizCode] = useState(() => deepLink.get("quizCode") || "");
  const [submissions, setSubmissions] = useState([]);
  const [assessmentSeries, setAssessmentSeries] = useState([]);
  const [workFilter, setWorkFilter] = useState("all");
  const [selectedAttemptByUser, setSelectedAttemptByUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [manualMarks, setManualMarks] = useState({});
  const [accessError, setAccessError] = useState(null);
  // Drawing answers the last marking run could not mark (evidence or
  // provider problems), and whether an earlier result was kept instead.
  const [drawingMarkingIssues, setDrawingMarkingIssues] = useState([]);
  const [markingProgress, setMarkingProgress] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: false,
    currentBatch: 0,
    totalBatches: 0,
    retryAttempts: 0,
    verificationPhase: false,
    verificationCompleted: 0,
    verificationFailed: 0
  });
  
  // Ref to track processed submissions for verification
  const processedSubmissionsRef = useRef(new Set());
  // Claimed synchronously on the first click so a second click during the
  // pre-flight access check cannot start a parallel marking run.
  const markingRunInFlightRef = useRef(false);

  // State to track total tokens used during AI marking
  const [totalTokensUsed, setTotalTokensUsed] = useState(0);
  
  // New state for UI enhancements
   const [searchTerm, setSearchTerm] = useState("");
   const [sortOption, setSortOption] = useState("score-asc");
   const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [quizTitle, setQuizTitle] = useState("");
  const [quizSubject, setQuizSubject] = useState("");
  const [expandedStudents, setExpandedStudents] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [userCache, setUserCache] = useState({});
  const [questionCache, setQuestionCache] = useState({});
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [selectedQuizQuestionIds, setSelectedQuizQuestionIds] = useState([]);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);
  const [generatingPdfFor, setGeneratingPdfFor] = useState("");
  const [pdfError, setPdfError] = useState(null);
  const [generatingAssessmentPdf, setGeneratingAssessmentPdf] = useState(false);
  const [assessmentReportError, setAssessmentReportError] = useState("");
  const [showTestChooser, setShowTestChooser] = useState(false);
  const [showKeyCorrection, setShowKeyCorrection] = useState(false);

  // Podium modal state
  const [showPodiumModal, setShowPodiumModal] = useState(false);
  const [podiumData, setPodiumData] = useState([]);
  const [podiumTrigger, setPodiumTrigger] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionSummary, setCompletionSummary] = useState({
    processed: 0,
    marked: 0,
    requiringAttention: 0,
    hasFailures: false,
  });
  const completionPrimaryRef = useRef(null);
  const completionReturnFocusRef = useRef(null);
  const podiumPrimaryRef = useRef(null);
  const podiumReturnFocusRef = useRef(null);

  // Configuration constants for batch processing - optimized values
  const BATCH_SIZE = 20; // Increased from 15 for better throughput
  const DELAY_BETWEEN_BATCHES = 500; // Reduced from 800ms for faster processing
  const MAX_RETRIES = 3; // Reduced from 5 - we'll handle failures more systematically
  const FIRESTORE_BATCH_LIMIT = 10; // Firestore has a limit of 10 items per batch get
  const VERIFICATION_BATCH_SIZE = 30; // Larger batch size for verification phase

  const navigate = useNavigate();
  const quizWideSubmissions = useMemo(
    () => quizWideSubmissionRecords(assessmentSeries),
    [assessmentSeries]
  );

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute(
      "data-dark-mode",
      darkMode ? "true" : "false"
    );
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  /**
   * Pre-flight access check: verify teacher can use AI marking
   * before starting any marking workflow.
   * 
   * Checks:
   * - teacherStatus === "expired" 
   * - teacherStatus === "trial" && trialEndsAt has passed
   * - creditsRemaining <= 0
   * 
   * Returns { allowed: boolean, message?: string, code?: string }
   */
  const checkTeacherAiAccessFrontend = useCallback(async () => {
    if (!user || !user.uid) {
      return {
        allowed: false,
        message: "You must be logged in to mark submissions.",
        code: "NOT_LOGGED_IN"
      };
    }

    // Get fresh user data from Firestore to check current status
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          allowed: false,
          message: "You must be logged in to mark submissions.",
          code: "NOT_LOGGED_IN"
        };
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        return {
          allowed: false,
          message: "User profile not found.",
          code: "USER_NOT_FOUND"
        };
      }

      const userData = userDocSnap.data();
      const userRole = normalizeUserRole(userData.role);
      if (userRole === "admin") {
        return { allowed: true };
      }

      const teacherStatus = userData.teacherStatus || "unknown";
      const trialEndsAt = userData.trialEndsAt;
      const creditsRemaining = userData.creditsRemaining || 0;

      // Check 1: If already marked expired
      if (teacherStatus === "expired") {
        return {
          allowed: false,
          message: "Your 4-week free trial has ended. Please contact contact@aiautomarker.com to continue using StudyDen.",
          code: "TRIAL_EXPIRED"
        };
      }

      // Check 2: If on trial, check if trial has ended
      if (teacherStatus === "trial" && trialEndsAt) {
        let trialEndDate;
        try {
          if (typeof trialEndsAt.toDate === "function") {
            // Firestore Timestamp
            trialEndDate = trialEndsAt.toDate();
          } else if (trialEndsAt instanceof Date) {
            trialEndDate = trialEndsAt;
          } else if (typeof trialEndsAt === "string") {
            // ISO string
            trialEndDate = new Date(trialEndsAt);
          } else {
            // Could not parse - block access
            return {
              allowed: false,
              message: "Unable to verify trial status. Please contact Ryan.",
              code: "ACCOUNT_INACTIVE"
            };
          }
        } catch (parseErr) {
          console.error("Error parsing trialEndsAt:", parseErr);
          return {
            allowed: false,
            message: "Unable to verify trial status. Please contact Ryan.",
            code: "ACCOUNT_INACTIVE"
          };
        }

        // Check if trial has expired
        const now = new Date();
        if (now > trialEndDate) {
          return {
            allowed: false,
            message: "Your 4-week free trial has ended. Please contact contact@aiautomarker.com to continue using StudyDen.",
            code: "TRIAL_EXPIRED"
          };
        }
      }

      // Check 3: If teacher status is neither trial nor active, block
      if (teacherStatus !== "trial" && teacherStatus !== "active") {
        return {
          allowed: false,
          message: "Your teacher account is not currently active.",
          code: "ACCOUNT_INACTIVE"
        };
      }

      // Check 4: If no credits remaining
      if (creditsRemaining <= 0) {
        return {
          allowed: false,
          message: "You have no credits remaining. Please contact Ryan to add more credits.",
          code: "NO_CREDITS"
        };
      }

      // All checks passed
      return { allowed: true };
    } catch (error) {
      console.error("Error checking teacher AI access:", error);
      return {
        allowed: false,
        message: "Error verifying account status. Please try again.",
        code: "CHECK_FAILED"
      };
    }
  }, [user]);

  /**
   * Determine if AI buttons should be disabled based on user status.
   * This is a synchronous check using already-loaded user data if available.
   */
  const isAiMarkingDisabled = useMemo(() => {
    if (user && normalizeUserRole(user.role) === "admin") {
      return false;
    }

    // If user data is available from props or state, check it
    if (user && user.teacherStatus === "expired") {
      return true;
    }
    if (user && user.teacherStatus === "trial" && user.trialEndsAt) {
      try {
        let trialEndDate;
        if (typeof user.trialEndsAt.toDate === "function") {
          trialEndDate = user.trialEndsAt.toDate();
        } else if (user.trialEndsAt instanceof Date) {
          trialEndDate = user.trialEndsAt;
        } else if (typeof user.trialEndsAt === "string") {
          trialEndDate = new Date(user.trialEndsAt);
        } else {
          return false;
        }
        const now = new Date();
        if (now > trialEndDate) {
          return true;
        }
      } catch (err) {
        console.error("Error parsing trial end date:", err);
      }
    }
    if (user && (user.creditsRemaining === 0 || user.creditsRemaining < 0)) {
      return true;
    }
    return false;
  }, [user]);

  // Load through the backend so ownership/admin authorization is enforced with
  // the verified Firebase identity, independently of the browser role value.
  const loadQuizResponses = async (selectedCode = quizCode) => {
    const normalizedQuizCode = selectedCode.trim();
    if (!normalizedQuizCode) {
      alert("Please enter a test code!");
      setLoading(false);
      return;
    }
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      alert("You must be logged in to view test responses.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setAccessError(null);
    setSubmissions([]);
    setExpandedStudents({});
    setExpandedQuestions({});
    setUserCache({});
    setQuestionCache({});
    setSelectedQuizId(null);
    setSelectedQuizQuestionIds([]);
    setQuizTitle("");
    setQuizSubject("");
    setAssessmentReportError("");
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/quiz-responses?quizCode=${encodeURIComponent(normalizedQuizCode)}`, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${idToken}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Failed to load test responses.");

      const responses = Array.isArray(data.responses) ? data.responses : [];
      setQuizTitle(data.quiz?.title || "");
      setQuizSubject(data.quiz?.subject || "");
      setSelectedQuizId(data.quiz?.id || null);
      setSelectedQuizQuestionIds(data.quiz?.questionIds || []);
      const series = buildAssessmentSeries({
        records: responses,
        finalResults: Array.isArray(data.finalResults) ? data.finalResults : [],
        attempts: Array.isArray(data.attempts) ? data.attempts : [],
        seriesStatuses: Array.isArray(data.seriesStatuses) ? data.seriesStatuses : [],
        defaultQuizId: data.quiz?.id || "",
      });
      const requestedStudentId = deepLink.get("studentId") || "";
      const requestedAttemptId = deepLink.get("attemptId") || "";
      const selections = Object.fromEntries(series.map((item) => [
        item.studentUid,
        selectDetailedAttemptId(
          item.attempts,
          item.finalResult,
          item.studentUid === requestedStudentId ? requestedAttemptId : ""
        ),
      ]));
      const selectedResponses = series.flatMap((item) =>
        item.attempts.find(({ id }) => id === selections[item.studentUid])?.records || []
      );
      setAssessmentSeries(withSeriesWorkTypes(series, data.seriesWorkTypes));
      setSelectedAttemptByUser(selections);
      setSubmissions(selectedResponses);
      setManualMarks(Object.fromEntries(responses.map((item) => [item.id, item.markAwarded])));
    } catch (error) {
      console.error("Error fetching quiz responses:", error);
      const message = error.message || "Failed to load test responses. Please try again.";
      setAccessError(message);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const selectStudentAttempt = useCallback((studentUid, attemptId) => {
    const series = assessmentSeries.find((item) => item.studentUid === studentUid);
    if (!series?.attempts.some((attempt) => attempt.id === attemptId)) return;
    const nextSelections = { ...selectedAttemptByUser, [studentUid]: attemptId };
    setSelectedAttemptByUser(nextSelections);
    setSubmissions(assessmentSeries.flatMap((item) =>
      item.attempts.find(({ id }) => id === nextSelections[item.studentUid])?.records || []
    ));
    setExpandedQuestions({});
  }, [assessmentSeries, selectedAttemptByUser]);

  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (!autoLoadedRef.current && deepLink.get("quizCode") && user) {
      autoLoadedRef.current = true;
      loadQuizResponses();
    }
    // The deep-link parameters are intentionally captured once on entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Retained temporarily as a reference for the legacy Firestore data-shaping path.
  // It is deliberately not called because browser-side ownership checks are insufficient.
  const loadQuizResponsesLegacy = async () => {
    if (!quizCode) {
      alert("Please enter a test code!");
      return;
    }

    // Require authentication before doing anything
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("You must be logged in to view test responses.");
      return;
    }

    try {
      setLoading(true);
      setSubmissions([]);
      setExpandedStudents({});
      setExpandedQuestions({});

      // Reset caches and scoped quiz state
      setUserCache({});
      setQuestionCache({});
      setSelectedQuizId(null);
      setSelectedQuizQuestionIds([]);

      console.log(`[VIEW QUIZ RESPONSES] Current teacher UID: ${currentUser.uid}`);
      console.log(`[VIEW QUIZ RESPONSES] Entered quiz code: ${quizCode}`);

      // ── Step 1: find the quiz that belongs to THIS teacher ──────────────────
      // NOTE: quiz documents store the code in the "quizCode" field (not "code")
      const quizzesRef = collection(db, "quizzes");
      const quizzesQuery = query(
        quizzesRef,
        where("creatorUid", "==", currentUser.uid),
        where("quizCode", "==", quizCode)
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);

      console.log(`[VIEW QUIZ RESPONSES] Matching quizzes for teacher: ${quizzesSnapshot.size}`);

      // Debug: log each matching quiz doc for diagnosis
      quizzesSnapshot.docs.forEach(d => {
        const qd = d.data();
        console.log(
          `[VIEW QUIZ RESPONSES] Matching quiz doc:\n` +
          `  id: ${d.id}\n` +
          `  quizCode: ${qd.quizCode}\n` +
          `  creatorUid: ${qd.creatorUid}\n` +
          `  questionIds length: ${(qd.questionIds || []).length}`
        );
      });

      if (quizzesSnapshot.empty) {
        alert("No test with that code was found for your account.");
        return; // finally block clears loading
      }

      if (quizzesSnapshot.size > 1) {
        alert(
          "More than one test with this code exists in your account. " +
          "Please rename/archive the duplicate or open responses from My Tests."
        );
        return; // finally block clears loading
      }

      const quizDoc = quizzesSnapshot.docs[0];
      const quizData = quizDoc.data();
      const resolvedQuizId = quizDoc.id;
      // Normalize questionIds: strip any "questions/" path prefix
      const resolvedQuestionIds = (quizData.questionIds || []).map(qid =>
        qid.includes("/") ? qid.split("/").pop() : qid
      );

      console.log(`[VIEW QUIZ RESPONSES] Selected quiz ID: ${resolvedQuizId}`);
      console.log(`[VIEW QUIZ RESPONSES] Selected quiz question count: ${resolvedQuestionIds.length}`);

      setQuizTitle(quizData.title || "");
      setSelectedQuizId(resolvedQuizId);
      setSelectedQuizQuestionIds(resolvedQuestionIds);

      // ── Step 2: load submissions, scoped to this teacher's quiz ─────────────
      const submissionsRef = collection(db, "submissions");
      let rawSubmissionDocs = [];
      let usingQuizIdQuery = false;
      let usingLegacyFallback = false;

      // Primary path: submissions that carry the Firestore quiz document ID
      const primaryQuery = query(submissionsRef, where("quizId", "==", resolvedQuizId));
      const primarySnapshot = await getDocs(primaryQuery);

      if (!primarySnapshot.empty) {
        usingQuizIdQuery = true;
        primarySnapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (!data.questionId) return;
          const extractedQuestionId = data.questionId.includes("/")
            ? data.questionId.split("/").pop()
            : data.questionId;
          rawSubmissionDocs.push({ id: docSnap.id, ...data, extractedQuestionId });
        });
      }

      // Legacy fallback: quizCode match + questionId fence (never quizCode alone)
      if (resolvedQuestionIds.length > 0) {
        const legacyQuery = query(submissionsRef, where("quizCode", "==", quizCode));
        const legacySnapshot = await getDocs(legacyQuery);

        console.log(`[VIEW QUIZ RESPONSES] Raw submissions found by quizCode: ${legacySnapshot.size}`);

        const alreadyCollected = new Set(rawSubmissionDocs.map(s => s.id));

        legacySnapshot.docs.forEach(docSnap => {
          if (alreadyCollected.has(docSnap.id)) return; // already captured via quizId
          const data = docSnap.data();
          if (!data.questionId) return;

          const extractedQuestionId = data.questionId.includes("/")
            ? data.questionId.split("/").pop()
            : data.questionId;

          // Safety fence: only include if the questionId belongs to THIS quiz
          if (resolvedQuestionIds.includes(extractedQuestionId)) {
            usingLegacyFallback = true;
            rawSubmissionDocs.push({ id: docSnap.id, ...data, extractedQuestionId });
          }
        });
      } else {
        // No questionIds on the quiz document — log raw quizCode count for diagnostics
        const legacyQuery = query(submissionsRef, where("quizCode", "==", quizCode));
        const legacySnapshot = await getDocs(legacyQuery);
        console.log(`[VIEW QUIZ RESPONSES] Raw submissions found by quizCode: ${legacySnapshot.size}`);
      }

      console.log(`[VIEW QUIZ RESPONSES] Using quizId query: ${usingQuizIdQuery}`);
      console.log(`[VIEW QUIZ RESPONSES] Using legacy questionId fallback: ${usingLegacyFallback}`);

      // Final safety filter: ensure every submission's question belongs to this quiz
      let submissionDocs = rawSubmissionDocs;
      if (resolvedQuestionIds.length > 0) {
        submissionDocs = rawSubmissionDocs.filter(s =>
          resolvedQuestionIds.includes(s.extractedQuestionId)
        );
      }

      const hiddenCount = rawSubmissionDocs.length - submissionDocs.length;
      console.log(`[VIEW QUIZ RESPONSES] Scoped submissions shown: ${submissionDocs.length}`);
      console.log(`[VIEW QUIZ RESPONSES] Hidden submissions excluded: ${hiddenCount}`);

      if (submissionDocs.length === 0) {
        return; // finally block clears loading
      }

      // Extract unique user IDs and question IDs for batch fetching
      const userIds = new Set();
      const questionIds = new Set();

      submissionDocs.forEach(submission => {
        userIds.add(submission.userId);
        questionIds.add(submission.extractedQuestionId);
      });

      // Batch fetch user data
      const userDataMap = {};
      const userIdArray = Array.from(userIds);
      
      for (let i = 0; i < userIdArray.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = userIdArray.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const usersQuery = query(collection(db, "users"), where(documentId(), "in", batch));
        const usersSnapshot = await getDocs(usersQuery);
        
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          userDataMap[doc.id] = {
            username: userData.username || userData.displayName || "Unknown Student"
          };
        });
      }
      
      // Handle any missing users
      userIdArray.forEach(userId => {
        if (!userDataMap[userId]) {
          userDataMap[userId] = { username: "Unknown Student" };
        }
      });
      
      // Update user cache
      setUserCache(userDataMap);

      // Batch fetch question data
      const questionDataMap = {};
      const questionIdArray = Array.from(questionIds);
      
      for (let i = 0; i < questionIdArray.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = questionIdArray.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const questionsQuery = query(collection(db, "questions"), where(documentId(), "in", batch));
        const questionsSnapshot = await getDocs(questionsQuery);
        
        questionsSnapshot.docs.forEach(doc => {
          questionDataMap[doc.id] = doc.data();
        });
      }
      
      // Update question cache
      setQuestionCache(questionDataMap);

      // Process submissions with the fetched data
      const responses = submissionDocs.map(submission => {
        const questionData = questionDataMap[submission.extractedQuestionId] || {};
        if (!questionData) return null; // Skip if question data not found
        
        const userData = userDataMap[submission.userId] || { username: "Unknown Student" };
        
        // For AI grading payload, if no question text exists, default to "Image-based question"
        const questionTextForAI =
          questionData.questionText && questionData.questionText.trim() !== ""
            ? questionData.questionText
            : (questionData.questionImageURL ? "Image-based question" : "Unknown Question");

        // For display purposes, if text exists use it; otherwise default to "Image-based question"
        const questionDisplay =
          questionData.questionText && questionData.questionText.trim() !== ""
            ? questionData.questionText
            : "Image-based question";

        return {
          id: submission.id,
          attemptId: submission.attemptId || null,
          attemptNumber: Number.isSafeInteger(submission.attemptNumber) ? submission.attemptNumber : 1,
          userId: submission.userId,
          username: userData.username,
          questionTextForAI,
          questionDisplay,
          questionImageURL: questionData.questionImageURL || "",
          ...(questionData.questionMedia ? { questionMedia: questionData.questionMedia } : {}),
          questionId: submission.extractedQuestionId,
          userAnswer: submission.userAnswer,
          ...(submission.drawingAnswer ? { drawingAnswer: submission.drawingAnswer } : {}),
          markSchemeAnswer: questionData.markSchemeAnswer || "N/A",
          feedback: submission.feedback ?? "Pending",
          maxMark: questionData.maxMark ?? "N/A",
          markAwarded: submission.markAwarded ?? "Pending",
          submittedAt: submission.submittedAt.toDate().toLocaleString(),
        };
      }).filter(Boolean); // Remove any null entries

      const latestResponses = selectLatestAttemptRecords(responses);
      setSubmissions(latestResponses);
      
      // Initialize manualMarks state based on the fetched responses
      const newManualMarks = {};
      latestResponses.forEach((response) => {
        newManualMarks[response.id] = response.markAwarded;
      });
      setManualMarks(newManualMarks);

    } catch (error) {
      console.error("Error fetching quiz responses:", error);
      alert("Failed to load test responses. Please try again.");
    } finally {
      // Always clear the loading spinner regardless of outcome
      setLoading(false);
    }
  };

  // Enhanced batch processing with improved retry logic and tracking
  const processBatchWithRetry = useCallback(async (
    batch,
    maxRetries,
    isRetry = false,
    isFirstRequest = false,
    actionType = "markAll"
  ) => {
    let retryAttempt = 0;
    let successful = [];
    let failed = [];
    let aiUsed = false;
    let currentBatch = [...batch];
    
    // Create a map to track individual submission status and errors
    const submissionStatus = new Map();
    batch.forEach(sub => {
      submissionStatus.set(sub.id, { 
        processed: false, 
        attempts: 0, 
        error: null 
      });
    });

    while (retryAttempt <= maxRetries && currentBatch.length > 0) {
      try {
        if (retryAttempt > 0 || isRetry) {
          setMarkingProgress(prev => ({
            ...prev,
            retryAttempts: prev.retryAttempts + 1
          }));
        }

        // Log batch size for debugging
        console.log(`Processing batch of ${currentBatch.length} submissions (attempt ${retryAttempt + 1})`);
        
        // Get Firebase ID token for authentication
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error("You must be logged in to mark submissions.");
        }
        const idToken = await currentUser.getIdToken();
        
        const response = await fetch(`${API_BASE_URL}/api/mark-quiz`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          },
          body: JSON.stringify({ 
            submissions: currentBatch.map(({ id }) => ({ id })),
            actionType: actionType,
            deductCredit: isFirstRequest,
            quizId: selectedQuizId
          }),
        });

        if (!response.ok) {
          // Handle 402 (no credits remaining) - do NOT retry
          if (response.status === 402) {
            const errorData = await response.json();
            alert(errorData.error || "No credits remaining");
            const requestError = new Error(errorData.error || "No credits remaining");
            requestError.nonRetryable = true;
            throw requestError;
          }
          // Handle other errors
          if (response.status === 403) {
            const errorData = await response.json();
            alert(errorData.error || "Not authorized to mark tests");
            const requestError = new Error(errorData.error || "Not authorized");
            requestError.nonRetryable = true;
            throw requestError;
          }
          if (response.status === 401) {
            alert("Authentication failed. Please log in again.");
            const requestError = new Error("Authentication failed");
            requestError.nonRetryable = true;
            throw requestError;
          }
          throw new Error(`API returned status ${response.status}`);
        }

        const responseData = await response.json();
        const processedSubmissions = responseData.submissions || responseData;
        aiUsed = aiUsed || responseData.usage?.aiUsed === true;
        
        // Track token usage if available
        if (responseData.usage && responseData.usage.total_tokens) {
          setTotalTokensUsed(prev => prev + responseData.usage.total_tokens);
        }
        
        // Track successfully processed submissions
        processedSubmissions.forEach(sub => {
          processedSubmissionsRef.current.add(sub.id);
          if (submissionStatus.has(sub.id)) {
            submissionStatus.get(sub.id).processed = true;
          }
        });
        
        successful = [...successful, ...processedSubmissions];
        currentBatch = [];
      } catch (error) {
        console.error(`Batch processing error (attempt ${retryAttempt + 1}/${maxRetries + 1}):`, error);
        
        // Increment attempt count for all submissions in this batch
        currentBatch.forEach(sub => {
          if (submissionStatus.has(sub.id)) {
            const status = submissionStatus.get(sub.id);
            status.attempts++;
            status.error = error.message;
          }
        });

        if (error.nonRetryable) {
          failed = [...failed, ...currentBatch];
          currentBatch = [];
          break;
        }
        
        if (retryAttempt === maxRetries) {
          // On final retry, log detailed failure information
          console.error("Failed submissions after max retries:", 
            currentBatch.map(sub => ({
              id: sub.id,
              attempts: submissionStatus.get(sub.id)?.attempts || 0,
              error: submissionStatus.get(sub.id)?.error || "Unknown error"
            }))
          );
          
          failed = [...failed, ...currentBatch];
          currentBatch = [];
        } else if (currentBatch.length > 3) {
          // Split batch in half for better error isolation
          const halfIndex = Math.ceil(currentBatch.length / 2);
          const firstHalf = currentBatch.slice(0, halfIndex);
          const secondHalf = currentBatch.slice(halfIndex);
          
          console.log(`Splitting batch into smaller chunks: ${firstHalf.length} and ${secondHalf.length}`);
          
          // Process first half
          const firstHalfResults = await processBatchWithRetry(
            firstHalf, 
            maxRetries - retryAttempt,
            true,
            isFirstRequest,
            actionType
          );
          
          // Process second half
          const secondHalfResults = await processBatchWithRetry(
            secondHalf, 
            maxRetries - retryAttempt,
            true,
            isFirstRequest && !firstHalfResults.aiUsed,
            actionType
          );
          
          successful = [...successful, ...firstHalfResults.successful, ...secondHalfResults.successful];
          failed = [...failed, ...firstHalfResults.failed, ...secondHalfResults.failed];
          aiUsed = aiUsed || firstHalfResults.aiUsed || secondHalfResults.aiUsed;
          currentBatch = [];
        } else {
          // For very small batches, try one by one as a last resort
          if (currentBatch.length > 1) {
            const oneByOneResults = await processOneByOne(currentBatch, isFirstRequest, actionType);
            successful = [...successful, ...oneByOneResults.successful];
            failed = [...failed, ...oneByOneResults.failed];
            aiUsed = aiUsed || oneByOneResults.aiUsed;
            currentBatch = [];
          } else {
            retryAttempt++;
            // Exponential backoff with jitter for retries
            const baseDelay = 1000 * Math.pow(1.5, retryAttempt);
            const jitter = Math.random() * 500;
            const delay = Math.min(baseDelay + jitter, 8000);
            console.log(`Retrying single submission after ${delay}ms delay`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    }

    return { successful, failed, aiUsed };
  }, [selectedQuizId]);

  // Enhanced one-by-one processing with better error handling and tracking
  const processOneByOne = useCallback(async (submissions, shouldDeductCredit = false, actionType = "markAll") => {
    const successful = [];
    const failed = [];
    let aiUsed = false;
    let creditAvailableForAi = shouldDeductCredit;
    
    console.log(`Processing ${submissions.length} submissions one by one`);

    for (const submission of submissions) {
      try {
        const currentUser = getAuth().currentUser;
        if (!currentUser) throw new Error("You must be logged in to mark submissions.");
        const idToken = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/mark-quiz`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({
            submissions: [{ id: submission.id }],
            quizId: selectedQuizId,
            deductCredit: creditAvailableForAi,
            actionType,
          }),
        });

        if (!response.ok) {
          // Handle token-related errors
          if (response.status === 403) {
            const errorData = await response.json();
            alert(errorData.error || "Insufficient tokens to complete this operation");
            throw new Error(errorData.error || "Insufficient tokens");
          }
          throw new Error(`API returned status ${response.status}`);
        }

        const responseData = await response.json();
        aiUsed = aiUsed || responseData.usage?.aiUsed === true;
        if (responseData.usage?.aiUsed === true) creditAvailableForAi = false;
        const processedSubmission = (responseData.submissions || responseData)[0];
        
        processedSubmissionsRef.current.add(processedSubmission.id);
        successful.push(processedSubmission);
        setMarkingProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
      } catch (error) {
        console.error(`Error processing submission ${submission.id}:`, error);
        failed.push(submission);
        
        setMarkingProgress(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }
      
      // Shorter delay between individual submissions
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    return { successful, failed, aiUsed };
  }, [selectedQuizId]);

  // Transaction-based Firestore update for better consistency
  const updateFirestoreWithTransaction = useCallback(async (submissions) => {
    const FIRESTORE_BATCH_SIZE = 500; // Firestore batch write limit is 500
    
    for (let i = 0; i < submissions.length; i += FIRESTORE_BATCH_SIZE) {
      const batchItems = submissions.slice(i, i + FIRESTORE_BATCH_SIZE);
      
      try {
        // Use a transaction for atomic updates
        await runTransaction(db, async (transaction) => {
          batchItems.forEach(sub => {
            const submissionRef = doc(db, "submissions", sub.id);
            transaction.update(submissionRef, {
              feedback: sub.feedback,
              markAwarded: sub.markAwarded,
            });
          });
        });
        
        console.log(`Successfully updated ${batchItems.length} submissions in Firestore`);
      } catch (error) {
        console.error(`Transaction error updating Firestore batch:`, error);
        
        // Fall back to batch writes if transaction fails
        try {
          const writeBatchObj = writeBatch(db);
          
          batchItems.forEach(sub => {
            const submissionRef = doc(db, "submissions", sub.id);
            writeBatchObj.update(submissionRef, {
              feedback: sub.feedback,
              markAwarded: sub.markAwarded,
            });
          });
          
          await writeBatchObj.commit();
          console.log(`Successfully updated ${batchItems.length} submissions using batch fallback`);
        } catch (batchError) {
          console.error(`Batch fallback error:`, batchError);
          throw batchError; // Re-throw to handle at higher level
        }
      }
    }
  }, []);

  // Enhanced state update with validation
  const updateSubmissionsState = useCallback((processedSubmissions) => {
    const processedById = new Map(processedSubmissions.map((submission) => [submission.id, submission]));
    const issues = processedSubmissions.filter((submission) => submission.markingError);
    setDrawingMarkingIssues((previous) => [
      ...previous.filter((issue) => !processedById.has(issue.id)),
      ...issues.map((submission) => ({ id: submission.id, kept: submission.previousResultKept === true })),
    ]);
    const applyMarkedFields = (submission) => {
      const processed = processedById.get(submission.id);
      return processed ? {
        ...submission,
        feedback: processed.feedback,
        markAwarded: processed.markAwarded,
      } : submission;
    };

    setSubmissions((previous) => previous.map(applyMarkedFields));
    setAssessmentSeries((previous) => previous.map((series) => ({
      ...series,
      records: series.records.map(applyMarkedFields),
      attempts: series.attempts.map((attempt) => ({
        ...attempt,
        records: attempt.records.map(applyMarkedFields),
      })),
    })));
    
    setManualMarks(prevMarks => {
      const newMarks = { ...prevMarks };
      processedSubmissions.forEach(sub => {
        newMarks[sub.id] = sub.markAwarded;
      });
      return newMarks;
    });
  }, []);

  // Verify all submissions were processed and retry any missed ones
  const verifyAndRetryMissedSubmissions = useCallback(async (allSubmissions, actionType) => {
    try {
      setMarkingProgress(prev => ({
        ...prev,
        verificationPhase: true,
        currentBatch: 1,
        totalBatches: 1
      }));
      
      console.log("Starting verification phase to check for missed submissions");
      
      // Find submissions that weren't processed
      const processedIds = processedSubmissionsRef.current;
      const missedSubmissions = allSubmissions.filter(sub => !processedIds.has(sub.id));
      
      if (missedSubmissions.length === 0) {
        console.log("Verification complete: All submissions were processed successfully");
        return { successful: [], failed: [] };
      }
      
      console.log(`Verification found ${missedSubmissions.length} missed submissions to retry`);
      
      setMarkingProgress(prev => ({
        ...prev,
        verificationCompleted: 0,
        verificationFailed: 0
      }));
      
      // Process missed submissions in larger batches
      const verificationBatches = [];
      for (let i = 0; i < missedSubmissions.length; i += VERIFICATION_BATCH_SIZE) {
        verificationBatches.push(missedSubmissions.slice(i, i + VERIFICATION_BATCH_SIZE));
      }
      
      setMarkingProgress(prev => ({
        ...prev,
        totalBatches: verificationBatches.length
      }));
      
      const verificationSuccessful = [];
      const verificationFailed = [];
      
      // Process each verification batch
      for (let batchIndex = 0; batchIndex < verificationBatches.length; batchIndex++) {
        setMarkingProgress(prev => ({
          ...prev,
          currentBatch: batchIndex + 1
        }));
        
        const currentBatch = verificationBatches[batchIndex];
        
        const batchResults = await processBatchWithRetry(
          currentBatch, 
          MAX_RETRIES,
          true,
          false,
          actionType
        );
        
        verificationSuccessful.push(...batchResults.successful);
        verificationFailed.push(...batchResults.failed);
        
        setMarkingProgress(prev => ({
          ...prev,
          verificationCompleted: verificationSuccessful.length,
          verificationFailed: verificationFailed.length
        }));
        
        updateSubmissionsState(batchResults.successful);
        
        if (batchIndex < verificationBatches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }
      
      return { successful: verificationSuccessful, failed: verificationFailed };
    } catch (error) {
      console.error("Error during verification phase:", error);
      return { successful: [], failed: [] };
    } finally {
      setMarkingProgress(prev => ({
        ...prev,
        verificationPhase: false
      }));
    }
  }, [VERIFICATION_BATCH_SIZE, DELAY_BETWEEN_BATCHES, MAX_RETRIES, processBatchWithRetry, updateSubmissionsState]);
  
  // Reload the complete authoritative projection after marking. Raw submission
  // reads cannot refresh attemptOutcome or the AO7 finalResult.
  const reloadAuthoritativeResults = useCallback(async () => {
    try {
      if (!selectedQuizId) return;
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error("You must be logged in to refresh assessment results.");
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/api/quiz-responses?quizId=${encodeURIComponent(selectedQuizId)}`,
        { cache: "no-store", headers: { Authorization: `Bearer ${idToken}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Result refresh failed");
      const responses = Array.isArray(data.responses) ? data.responses : [];
      const series = buildAssessmentSeries({
        records: responses,
        finalResults: Array.isArray(data.finalResults) ? data.finalResults : [],
        attempts: Array.isArray(data.attempts) ? data.attempts : [],
        seriesStatuses: Array.isArray(data.seriesStatuses) ? data.seriesStatuses : [],
        defaultQuizId: selectedQuizId,
      });
      const selections = Object.fromEntries(series.map((item) => {
        const retained = selectedAttemptByUser[item.studentUid];
        return [item.studentUid, selectDetailedAttemptId(item.attempts, item.finalResult, retained)];
      }));
      setAssessmentSeries(withSeriesWorkTypes(series, data.seriesWorkTypes));
      setSelectedAttemptByUser(selections);
      setSubmissions(series.flatMap((item) =>
        item.attempts.find(({ id }) => id === selections[item.studentUid])?.records || []
      ));
      setManualMarks(Object.fromEntries(responses.map((item) => [item.id, item.markAwarded])));
    } catch (error) {
      console.error("Error reloading authoritative assessment results:", error);
      throw error;
    }
  }, [selectedQuizId, selectedAttemptByUser]);

   // Enhanced marking function with verification and state reconciliation
   const runMarkSubmissionsWithAI = useCallback(async (submissionsToMark, actionType = "markAll") => {
     setDrawingMarkingIssues([]);
      if (quizWideSubmissions.length === 0) {
       alert("No responses to mark!");
       return;
     }

     if (!user || !user.uid) {
       alert("You must be logged in to mark submissions.");
       return;
     }

     if (submissionsToMark.length === 0) {
       const confirmRemark = window.confirm("All submissions are already marked, are you sure you want to mark again?");
       if (!confirmRemark) {
         return;
       }
     }

     // ========== PRE-FLIGHT ACCESS CHECK ==========
     // Check teacher status BEFORE starting any marking workflow
     // This prevents the progress UI from showing if access is denied
     console.log("[MARKING] Running pre-flight access check...");
     const accessCheckResult = await checkTeacherAiAccessFrontend();
     
      if (!accessCheckResult.allowed && accessCheckResult.code !== "NO_CREDITS") {
       // Access denied - show the error message and STOP
       console.error(`[MARKING] Access check failed: ${accessCheckResult.code} - ${accessCheckResult.message}`);
       alert(accessCheckResult.message);
       setAccessError(accessCheckResult.message);
       return; // Exit without starting marking
     }
     
      console.log(
        accessCheckResult.code === "NO_CREDITS"
          ? "[MARKING] Deferring the AI credit decision to authoritative server classification."
          : "[MARKING] Access check passed. Proceeding with marking workflow."
      );

     // Reset the processed submissions tracking
     processedSubmissionsRef.current = new Set();
     
     // Reset token counter for this marking session
     setTotalTokensUsed(0);
     
     // Only NOW set marking state after access check passes
     setMarking(true);
     
     const preparedSubmissions = submissionsToMark.map(sub => ({
       ...sub,
       questionText: sub.questionTextForAI,
     }));
     
     // Create batches for processing
     const batches = [];
     for (let i = 0; i < preparedSubmissions.length; i += BATCH_SIZE) {
       batches.push(preparedSubmissions.slice(i, i + BATCH_SIZE));
     }
     
     setMarkingProgress({
       total: preparedSubmissions.length,
       completed: 0,
       failed: 0,
       inProgress: true,
       currentBatch: 0,
       totalBatches: batches.length,
       retryAttempts: 0,
       verificationPhase: false,
       verificationCompleted: 0,
       verificationFailed: 0
     });

     try {
       console.log(`Starting to process ${preparedSubmissions.length} submissions in ${batches.length} batches`);
       
       const successfulSubmissions = [];
       const failedSubmissions = [];
       
       // Track if this is the first API request for credit deduction
       let isFirstRequest = true;
       
       // Process each batch
       for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
         setMarkingProgress(prev => ({
           ...prev,
           currentBatch: batchIndex + 1
         }));
         
         const currentBatch = batches[batchIndex];
         
         const batchResults = await processBatchWithRetry(
           currentBatch, 
           MAX_RETRIES,
           false,
            isFirstRequest,
            actionType
         );
         
          // Keep the one-credit action flag available until a batch actually uses AI.
          if (batchResults.aiUsed) isFirstRequest = false;
        
        successfulSubmissions.push(...batchResults.successful);
        failedSubmissions.push(...batchResults.failed);
        
        setMarkingProgress(prev => ({
          ...prev,
          completed: successfulSubmissions.length,
          failed: failedSubmissions.length
        }));
        
        updateSubmissionsState(batchResults.successful);
        
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES));
        }
      }
      
      // Retry failed submissions with increased priority
      if (failedSubmissions.length > 0) {
        console.log(`Retrying ${failedSubmissions.length} failed submissions`);
        
        setMarkingProgress(prev => ({
          ...prev,
          currentBatch: batches.length + 1,
          totalBatches: batches.length + 1
        }));
        
        const retryResults = await processBatchWithRetry(
          failedSubmissions, 
          MAX_RETRIES,
          true,
          false,
          actionType
        );
        
        successfulSubmissions.push(...retryResults.successful);
        
        updateSubmissionsState(retryResults.successful);
        
        setMarkingProgress(prev => ({
          ...prev,
          completed: prev.completed + retryResults.successful.length,
          failed: retryResults.failed.length
        }));
      }
      
      // Verification phase - check for any missed submissions
      console.log("Starting verification phase to ensure all submissions were processed");
      const verificationResults = await verifyAndRetryMissedSubmissions(preparedSubmissions, actionType);
      
      if (verificationResults.successful.length > 0) {
        console.log(`Verification phase successfully processed ${verificationResults.successful.length} missed submissions`);
        successfulSubmissions.push(...verificationResults.successful);
        updateSubmissionsState(verificationResults.successful);
      }
      
      // Final database reload to ensure UI state matches Firestore
      await reloadAuthoritativeResults();

      // Show the existing completion outcome using the StudyDen modal.
      setCompletionSummary(summarizeMarkingRun(preparedSubmissions, successfulSubmissions));
      setShowCompletionModal(true);
      
    } catch (error) {
      console.error("Error with AI grading:", error);
      alert("Failed to mark responses with AI.");
    } finally {
      setMarking(false);
      setMarkingProgress(prev => ({ ...prev, inProgress: false }));
    }
  }, [
    quizWideSubmissions,
    BATCH_SIZE, 
    DELAY_BETWEEN_BATCHES, 
    MAX_RETRIES, 
    processBatchWithRetry, 
    updateSubmissionsState, 
    verifyAndRetryMissedSubmissions, 
    reloadAuthoritativeResults,
    user,
    checkTeacherAiAccessFrontend
  ]);

  const markSubmissionsWithAI = useCallback(async (submissionsToMark, actionType) => {
    if (markingRunInFlightRef.current) return;
    markingRunInFlightRef.current = true;
    try {
      await runMarkSubmissionsWithAI(submissionsToMark, actionType);
    } finally {
      markingRunInFlightRef.current = false;
    }
  }, [runMarkSubmissionsWithAI]);

  // Mark ALL submissions
  const markAllWithAI = useCallback(() => {
    markSubmissionsWithAI(quizWideSubmissions, "markAll");
  }, [quizWideSubmissions, markSubmissionsWithAI]);

  // Mark ONLY unmarked submissions
  const markAllUnmarkedWithAI = useCallback(() => {
    const unmarkedSubmissions = unmarkedSubmissionRecords(quizWideSubmissions);
    markSubmissionsWithAI(unmarkedSubmissions, "markUnmarked");
  }, [quizWideSubmissions, markSubmissionsWithAI]);

  // Group and filter submissions by userId - optimized with useMemo
  const filteredAndSortedUsers = useMemo(() => {
    // Group submissions by user
    const groupedByUser = assessmentSeries.reduce((acc, series) => {
      acc[series.studentUid] = {
        userId: series.studentUid,
        username: series.studentName,
        items: [],
        attempts: series.attempts,
        finalResult: series.finalResult,
        summary: series.summary,
        displayStatus: series.displayStatus,
        selectedAttemptId: selectedAttemptByUser[series.studentUid] || series.selectedAttemptId,
        workType: series.workType === undefined ? "assessment" : series.workType,
      };
      return acc;
    }, {});
    submissions.forEach((sub) => {
      const { userId } = sub;
      if (!groupedByUser[userId]) {
        groupedByUser[userId] = {
          userId,
          username: sub.username,
          items: [],
        };
      }
      groupedByUser[userId].items.push(sub);
    });

    let usersArray = Object.values(groupedByUser);

    // Pre-calculate scores for each user to avoid recalculating during sort
    usersArray = usersArray.map(user => {
      const totalMarks = user.items.reduce(
        (acc, s) => acc + (parseFloat(s.markAwarded) || 0), 0
      );
      const totalMaxMarks = user.items.reduce(
        (acc, s) => acc + (parseFloat(s.maxMark) || 0), 0
      );
      const scorePercentage = Number.isFinite(user.finalResult?.percentage)
        ? user.finalResult.percentage
        : totalMaxMarks > 0 ? (totalMarks / totalMaxMarks) * 100 : 0;
      
      return {
        ...user,
        totalMarks,
        totalMaxMarks,
        scorePercentage
      };
    });

    usersArray = usersArray.filter((user) => matchesWorkType(user.workType === undefined ? "assessment" : user.workType, workFilter));

    // Filter by search term
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      usersArray = usersArray.filter(user => 
        user.username.toLowerCase().includes(searchLower)
      );
    }

    // Sort users
    usersArray.sort((a, b) => {
      switch (sortOption) {
        case "name-asc":
          return a.username.localeCompare(b.username);
        case "name-desc":
          return b.username.localeCompare(a.username);
        case "score-desc":
          return a.scorePercentage - b.scorePercentage;
        case "score-asc":
          return b.scorePercentage - a.scorePercentage;
        default:
          return 0;
      }
    });

    return usersArray;
  }, [submissions, assessmentSeries, selectedAttemptByUser, searchTerm, sortOption, workFilter]);

  const responseMetrics = useMemo(() => {
    const studentCount = new Set(
      quizWideSubmissions.map((submission) => submission.userId).filter(Boolean)
    ).size;
    const awaitingCount = unmarkedSubmissionRecords(quizWideSubmissions).length;

    return {
      studentCount,
      responseCount: quizWideSubmissions.length,
      markedCount: quizWideSubmissions.length - awaitingCount,
      awaitingCount,
    };
  }, [quizWideSubmissions]);
  
  // Pagination for student list - optimized with useMemo
  const { currentUsers, totalPages } = useMemo(() => {
    const indexOfLastUser = currentPage * itemsPerPage;
    const indexOfFirstUser = indexOfLastUser - itemsPerPage;
    const paginatedUsers = filteredAndSortedUsers.slice(indexOfFirstUser, indexOfLastUser);
    const pages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
    
    return {
      currentUsers: paginatedUsers,
      totalPages: pages,
    };
  }, [filteredAndSortedUsers, currentPage, itemsPerPage]);

  // Download an attempt-aware spreadsheet from the authorized result projection.
  const downloadCSV = useCallback(() => {
    if (assessmentSeries.length === 0) {
      alert("No data to download.");
      return;
    }
    const csvContent = buildAssessmentResultsCsv({
      assessmentTitle: quizTitle || quizCode,
      subject: quizSubject,
      quizCode: quizCode.trim(),
      questionIds: selectedQuizQuestionIds,
      assessmentSeries,
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.style.display = "none";
    link.download = assessmentResultsCsvFilename(quizTitle || quizCode);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [assessmentSeries, quizCode, quizSubject, quizTitle, selectedQuizQuestionIds]);

  // Toggle student expansion - optimized with useCallback
  const toggleStudentExpansion = useCallback((userId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  }, []);

  // Toggle question expansion - new function
  const toggleQuestionExpansion = useCallback((questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  }, []);

  // Handle manual mark updates - optimized with useCallback
  const handleMarkChange = useCallback((submissionId, value) => {
    setManualMarks((prev) => ({
      ...prev,
      [submissionId]: value,
    }));
  }, []);

  // ==================== Podium Modal Logic ====================

  // Compute top 3 distinct score groups from the current submissions.
  // Students with equal percentages share the same podium position.
  const getTopPerformers = useCallback((currentSubmissions) => {
    // Group by user
    const groupedByUser = currentSubmissions.reduce((acc, sub) => {
      const { userId } = sub;
      if (!acc[userId]) {
        acc[userId] = { userId, username: sub.username, items: [] };
      }
      acc[userId].items.push(sub);
      return acc;
    }, {});

    // Calculate scores, keeping only students with valid marks
    const scored = Object.values(groupedByUser)
      .map((u) => {
        // Only count submissions with a real numeric mark
        const markedItems = u.items.filter(
          (s) => s.markAwarded !== "Pending" && s.markAwarded !== "N/A" && !isNaN(parseFloat(s.markAwarded))
        );
        if (markedItems.length === 0) return null;

        const totalMarks = markedItems.reduce((acc, s) => acc + (parseFloat(s.markAwarded) || 0), 0);
        const totalMaxMarks = u.items.reduce((acc, s) => acc + (parseFloat(s.maxMark) || 0), 0);
        const scorePercentage = totalMaxMarks > 0 ? (totalMarks / totalMaxMarks) * 100 : 0;

        return { ...u, totalMarks, totalMaxMarks, scorePercentage };
      })
      .filter(Boolean);

    // Sort: highest percentage first; use total marks then name only for stable display order
    scored.sort((a, b) =>
      b.scorePercentage !== a.scorePercentage
        ? b.scorePercentage - a.scorePercentage
        : b.totalMarks !== a.totalMarks
          ? b.totalMarks - a.totalMarks
          : (a.username || "").localeCompare(b.username || "")
    );

    const scoreGroups = [];

    scored.forEach((student) => {
      const scoreKey = student.scorePercentage.toFixed(4);
      let group = scoreGroups.find((g) => g.scoreKey === scoreKey);

      if (!group) {
        group = {
          rank: scoreGroups.length + 1,
          scoreKey,
          scorePercentage: student.scorePercentage,
          performers: []
        };
        scoreGroups.push(group);
      }

      group.performers.push(student);
    });

    return scoreGroups.slice(0, 3).map((group) => {
      const sameRawScore = group.performers.every((student) =>
        student.totalMarks === group.performers[0].totalMarks &&
        student.totalMaxMarks === group.performers[0].totalMaxMarks
      );

      return {
        ...group,
        totalMarks: sameRawScore ? group.performers[0].totalMarks : null,
        totalMaxMarks: sameRawScore ? group.performers[0].totalMaxMarks : null
      };
    });
  }, []);

  const closeCompletionModal = useCallback(() => {
    setShowCompletionModal(false);
  }, []);

  const handleViewTopPerformers = useCallback(() => {
    setShowCompletionModal(false);
    setPodiumTrigger(true);
  }, []);

  useEffect(() => {
    if (!showCompletionModal) return;
    completionReturnFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => completionPrimaryRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeCompletionModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
      completionReturnFocusRef.current?.focus?.();
    };
  }, [showCompletionModal, closeCompletionModal]);

  // Open the existing podium flow when requested from the completion modal.
  useEffect(() => {
    if (!podiumTrigger) return;
    setPodiumTrigger(false);

    const performers = getTopPerformers(submissions);
    if (performers.length > 0) {
      setPodiumData(performers);
      setShowPodiumModal(true);
    }
  }, [podiumTrigger, submissions, getTopPerformers]);

  // Escape key closes the modal
  useEffect(() => {
    if (!showPodiumModal) return;
    podiumReturnFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => podiumPrimaryRef.current?.focus(), 0);
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowPodiumModal(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", handleKeyDown);
      podiumReturnFocusRef.current?.focus?.();
    };
  }, [showPodiumModal]);

  const getPodiumRankLabel = (rank) => {
    if (rank === 1) return "1st";
    if (rank === 2) return "2nd";
    if (rank === 3) return "3rd";
    return `${rank}th`;
  };

  const renderPodiumSpot = (group) => {
    if (!group) return null;
    const visiblePerformers = group.performers.slice(0, 4);
    const hiddenCount = group.performers.length - visiblePerformers.length;
    const rankLabel = getPodiumRankLabel(group.rank);
    const jointLabel = group.performers.length > 1 ? `Joint ${rankLabel}` : rankLabel;
    const scoreText = group.totalMaxMarks
      ? `${group.totalMarks}/${group.totalMaxMarks}`
      : "Shared percentage";

    return (
      <article className={`vqr-performer-card vqr-performer-rank-${group.rank}`} key={group.rank}>
        <div className="vqr-performer-icon" aria-hidden="true"><Trophy size={22} /></div>
        <p className="vqr-performer-rank">{jointLabel} Place</p>
        <div className="vqr-performer-names">
          {visiblePerformers.map((student) => (
            <div className="vqr-performer-name" key={student.userId}>
              {student.username}
            </div>
          ))}
          {hiddenCount > 0 && (
            <div className="vqr-performer-more">+{hiddenCount} more</div>
          )}
        </div>
        <div className="vqr-performer-stats">
          <strong>{scoreText}</strong>
          <span>{`${group.scorePercentage.toFixed(1)}%`}</span>
        </div>
      </article>
    );
  };

  // ==================== End Podium Logic ====================

  // Save manual mark updates to Firestore - optimized with useCallback
  const saveManualMark = useCallback(async (submissionId) => {
    try {
      const mark = manualMarks[submissionId];
      if (mark === undefined) return;
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error("You must be logged in to update marks.");
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/quiz-responses/${encodeURIComponent(submissionId)}/mark`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ quizId: selectedQuizId, markAwarded: mark })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Failed to update mark");

      // Reload authoritative attempt outcomes and AO7 final result. The selected
      // attempt remains selected when it is still valid.
      const refreshResponse = await fetch(
        `${API_BASE_URL}/api/quiz-responses?quizId=${encodeURIComponent(selectedQuizId)}`,
        { cache: "no-store", headers: { Authorization: `Bearer ${idToken}` } }
      );
      const refreshData = await refreshResponse.json().catch(() => ({}));
      if (!refreshResponse.ok) throw new Error(refreshData.error || "Result refresh failed");
      const responses = Array.isArray(refreshData.responses) ? refreshData.responses : [];
      const series = buildAssessmentSeries({
        records: responses,
        finalResults: Array.isArray(refreshData.finalResults) ? refreshData.finalResults : [],
        attempts: Array.isArray(refreshData.attempts) ? refreshData.attempts : [],
        seriesStatuses: Array.isArray(refreshData.seriesStatuses) ? refreshData.seriesStatuses : [],
        defaultQuizId: selectedQuizId,
      });
      const selections = Object.fromEntries(series.map((item) => {
        const retained = selectedAttemptByUser[item.studentUid];
        return [item.studentUid, selectDetailedAttemptId(item.attempts, item.finalResult, retained)];
      }));
      setAssessmentSeries(withSeriesWorkTypes(series, refreshData.seriesWorkTypes));
      setSelectedAttemptByUser(selections);
      setSubmissions(series.flatMap((item) =>
        item.attempts.find(({ id }) => id === selections[item.studentUid])?.records || []
      ));
      setManualMarks(Object.fromEntries(responses.map((item) => [item.id, item.markAwarded])));

      // Use a less intrusive notification method
      console.log("Mark updated successfully!");
    } catch (error) {
      console.error("Error updating mark:", error);
      alert("Failed to update mark. Please try again.");
    }
  }, [manualMarks, selectedQuizId, selectedAttemptByUser]);

  const downloadFeedbackPdf = useCallback(async (student) => {
    if (!selectedQuizId || !student?.userId || !student.selectedAttemptId || generatingPdfFor) return;
    setGeneratingPdfFor(student.userId);
    setPdfError(null);
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error("You must be logged in to generate a feedback report.");
      const idToken = await currentUser.getIdToken();
      const params = new URLSearchParams({
        quizId: selectedQuizId,
        studentId: student.userId,
        attemptId: student.selectedAttemptId,
      });
      const response = await fetch(`${API_BASE_URL}/api/assessment-feedback-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.error || "The feedback PDF could not be generated.");
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const safeFilePart = (value, fallback) => String(value || fallback)
        .normalize("NFKD").replace(/[^a-zA-Z0-9 _-]/g, "").trim().replace(/\s+/g, "_").slice(0, 80) || fallback;
      const fallbackFilename = `StudyDen_${safeFilePart(student.username, "Student")}_${safeFilePart(quizTitle || quizCode, "Assessment")}_Feedback.pdf`;
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1] || fallbackFilename;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading feedback PDF:", error);
      setPdfError({ userId: student.userId, message: error.message || "The feedback PDF could not be generated." });
    } finally {
      setGeneratingPdfFor("");
    }
  }, [generatingPdfFor, quizCode, quizTitle, selectedQuizId]);

  const downloadAssessmentPdf = useCallback(async () => {
    if (!selectedQuizId || generatingAssessmentPdf) return;
    setGeneratingAssessmentPdf(true);
    setAssessmentReportError("");
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) throw new Error("You must be logged in to generate an assessment report.");
      const idToken = await currentUser.getIdToken();
      const params = new URLSearchParams({ quizId: selectedQuizId });
      const response = await fetch(`${API_BASE_URL}/api/assessment-results-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.error || "The assessment PDF report could not be generated.");
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") || "";
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1]
        || assessmentResultsCsvFilename(quizTitle || quizCode).replace(/_Results\.csv$/, "_Results_Report.pdf");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating assessment results report:", error);
      setAssessmentReportError(error.message || "The assessment PDF report could not be generated.");
    } finally {
      setGeneratingAssessmentPdf(false);
    }
  }, [generatingAssessmentPdf, quizCode, quizTitle, selectedQuizId]);

  const handleLogout = async () => {
    try {
      await signOut(getAuth());
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (copyError) {
      console.error("Could not copy teacher code", copyError);
    }
  };

  const role = user?.role === "admin" ? "admin" : "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");

  const initialsForStudent = (value) =>
    String(value || "Student")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "S";

  // Render the component
  return (
    <div className={`sd-dashboard sd-brand-theme sd-role-${role} vqr-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role={role}
        username={username}
        officialDashboardBrand
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/view-quiz-responses"
      />

      <div className="sd-workspace vqr-workspace">
        <DashboardHeader
          username={username}
          role={role}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={searchTerm}
          onSearch={setSearchTerm}
        />

        <main className="sd-main vqr-main">
          <section className="vqr-hero">
            <span className="vqr-hero-corner vqr-hero-corner-top" aria-hidden="true" />
            <span className="vqr-hero-corner vqr-hero-corner-bottom" aria-hidden="true" />
            <div className="vqr-hero-copy">
              <div className="vqr-context-actions">
                <button type="button" onClick={() => navigate(-1)} className="vqr-back-link">
                  <ArrowLeft size={15} />Back
                </button>
                <button type="button" onClick={() => navigate("/view-quizzes")} className="vqr-back-link">
                  My Tests
                </button>
              </div>
              <p className="vqr-eyebrow"><Sparkles size={14} />AI Marking</p>
              <h1>Test Responses</h1>
              <p>Load student submissions, review responses, and manage AI-assisted marking.</p>
            </div>
            <div className="vqr-hero-art" aria-hidden="true">
              <span><BarChart3 size={34} /></span>
              <i><ClipboardCheck size={18} /></i>
            </div>
          </section>

          {/* Quiz Code Input Card */}
          <section className="vqr-card vqr-load-card" aria-labelledby="vqr-load-title">
            <div className="vqr-section-heading">
              <span className="vqr-icon-tile vqr-tone-blue"><KeyRound size={21} /></span>
              <div>
                <p>Load assessment</p>
                <h2 id="vqr-load-title">Enter Test Code</h2>
                <span>Enter the code for the assessment whose submissions you want to review.</span>
              </div>
            </div>

            <div className="vqr-load-form">
              <label className="vqr-field" htmlFor="vqr-quiz-code">
                <span>Test code</span>
                <div className="vqr-input-wrap">
                  <KeyRound size={17} aria-hidden="true" />
                  <input
                    id="vqr-quiz-code"
                    type="text"
                    value={quizCode}
                    onChange={(e) => setQuizCode(e.target.value)}
                    placeholder="Enter test code"
                    className="text-input"
                  />
                </div>
              </label>
              <button
                onClick={() => loadQuizResponses()}
                disabled={loading || marking}
                className="primary-button vqr-load-button"
                type="button"
              >
                <Search size={17} />
                {loading ? "Loading…" : "Load Responses"}
              </button>
            </div>
            <div className="vqr-load-alternative"><span>or</span>
              <button type="button" className="vqr-choose-test-button" onClick={() => setShowTestChooser(true)} disabled={loading || marking}>
                <BookOpen size={17} />Choose from My Tests
              </button></div>
          </section>
            
          {selectedQuizId && !loading && (
            <>
              <section className="vqr-card vqr-assessment-overview" aria-labelledby="vqr-assessment-title">
                <div className="vqr-section-heading">
                  <span className="vqr-icon-tile vqr-tone-purple"><ClipboardCheck size={21} /></span>
                  <div>
                    <p>Assessment overview</p>
                    <h2 id="vqr-assessment-title">{quizTitle || "Loaded assessment"}</h2>
                    <span>Test code <strong>{quizCode.trim()}</strong></span>
                  </div>
                </div>
                <div className="vqr-assessment-meta">
                  <span><FileQuestion size={16} />{selectedQuizQuestionIds.length} questions</span>
                  <span><UsersRound size={16} />{responseMetrics.studentCount} students</span>
                </div>
              </section>

              {quizWideSubmissions.length > 0 && (
                <section className="vqr-metrics" aria-label="Response summary">
                  <article className="vqr-metric vqr-metric-blue">
                    <span><UsersRound size={20} /></span>
                    <div><strong>{responseMetrics.studentCount}</strong><p>Students</p></div>
                  </article>
                  <article className="vqr-metric vqr-metric-cyan">
                    <span><FileQuestion size={20} /></span>
                    <div><strong>{responseMetrics.responseCount}</strong><p>Responses</p></div>
                  </article>
                  <article className="vqr-metric vqr-metric-green">
                    <span><CheckCircle2 size={20} /></span>
                    <div><strong>{responseMetrics.markedCount}</strong><p>Marked</p></div>
                  </article>
                  <article className="vqr-metric vqr-metric-amber">
                    <span><Sparkles size={20} /></span>
                    <div><strong>{responseMetrics.awaitingCount}</strong><p>Awaiting marking</p></div>
                  </article>
                </section>
              )}

              {quizWideSubmissions.length > 0 && (
                <section className="vqr-card vqr-marking-toolbar" aria-labelledby="vqr-marking-title">
                  <div className="vqr-toolbar-heading">
                    <div className="vqr-section-heading">
                      <span className="vqr-icon-tile vqr-tone-teal"><Sparkles size={21} /></span>
                      <div>
                        <p>AI-assisted workflow</p>
                        <h2 id="vqr-marking-title">Marking controls</h2>
                        <span>Review all responses or process only those still awaiting marks.</span>
                      </div>
                    </div>
                    <div className="button-container">
                      <button
                        onClick={markAllWithAI}
                        disabled={loading || marking || isAiMarkingDisabled}
                        className="red-button vqr-ai-primary"
                        title={isAiMarkingDisabled ? "Trial expired - AI marking unavailable" : ""}
                        type="button"
                      >
                        <Sparkles size={17} />Mark All with AI
                      </button>
                      <button
                        onClick={markAllUnmarkedWithAI}
                        disabled={loading || marking || isAiMarkingDisabled}
                        className="blue-button"
                        title={isAiMarkingDisabled ? "Trial expired - AI marking unavailable" : ""}
                        type="button"
                      >
                        <CheckCircle2 size={17} />Mark Unmarked with AI
                      </button>
                      <button
                        onClick={downloadAssessmentPdf}
                        disabled={loading || marking || generatingAssessmentPdf}
                        className="dark-button"
                        type="button"
                      >
                        <Download size={17} />{generatingAssessmentPdf ? "Generating PDF..." : "Download PDF Report"}
                      </button>
                      <button
                        onClick={downloadCSV}
                        disabled={loading || marking}
                        className="dark-button"
                        type="button"
                      >
                        <Download size={17} />Download CSV
                      </button>
                      <button
                        onClick={() => setShowKeyCorrection(true)}
                        disabled={loading || marking || !selectedQuizId}
                        className="dark-button"
                        type="button"
                      >
                        <ListChecks size={17} />Correct an answer key
                      </button>
                    </div>
                  </div>
                  {assessmentReportError && <p className="vqr-export-error" role="alert">{assessmentReportError}</p>}

                  <div className="search-sort-container">
                    <label className="vqr-search-field">
                      <Search size={17} aria-hidden="true" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search students..."
                        className="search-input"
                        aria-label="Search students"
                      />
                    </label>
                    <label className="vqr-sort-field">
                      <span>Sort by</span>
                      <select
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="sort-select"
                        aria-label="Sort students"
                      >
                        <option value="score-asc">Score (High-Low)</option>
                        <option value="score-desc">Score (Low-High)</option>
                        <option value="name-asc">Name (A-Z)</option>
                        <option value="name-desc">Name (Z-A)</option>
                      </select>
                      <ChevronDown size={15} aria-hidden="true" />
                    </label>
                  </div>
                </section>
              )}
            </>
          )}

          {/* Loading State */}
          {loading && (
            <div className="vqr-card loading-container" role="status" aria-live="polite">
              <div className="spinner"></div>
              <p className="loading-text">Loading test responses...</p>
            </div>
          )}

          {!loading && accessError && (
            <div className="error-text vqr-error-panel" role="alert">
              <strong>Unable to load assessment</strong>
              <span>{accessError}</span>
            </div>
          )}

          {/* Marking Progress */}
          {marking && markingProgress.inProgress && (
            <AiMarkingProgressPanel
              progress={markingProgress}
              processedCount={processedSubmissionsRef.current.size}
            />
          )}

          {drawingMarkingIssues.length > 0 && (
            <div className="drawing-marking-issues" role="alert">
              <strong>
                {drawingMarkingIssues.length} {drawingMarkingIssues.length === 1 ? "answer with a drawing was" : "answers with drawings were"} not marked.
              </strong>
              <span>
                {drawingMarkingIssues.some((issue) => !issue.kept) && " Answers without an earlier mark are left unmarked (not zero) and can be retried with Mark Unmarked."}
                {drawingMarkingIssues.some((issue) => issue.kept) && " Answers that already had a mark keep their earlier mark and feedback."}
              </span>
            </div>
          )}

          {/* Student Responses */}
          {!loading && !marking && assessmentSeries.length > 0 && (
            <section className="vqr-card vqr-responses-card" aria-labelledby="vqr-responses-title">
              <div className="vqr-responses-heading">
                <div className="vqr-section-heading">
                  <span className="vqr-icon-tile vqr-tone-blue"><UsersRound size={21} /></span>
                  <div>
                    <p>Review workspace</p>
                    <h2 id="vqr-responses-title">Student Responses</h2>
                    <span>Expand a student, then open each question to review marks and feedback.</span>
                  </div>
                </div>
                <span className="vqr-result-count">{filteredAndSortedUsers.length} students</span>
              </div>
              <WorkTypeFilter value={workFilter} className="vqr-work-filter"
                onChange={(value) => { setWorkFilter(value); setCurrentPage(1); }} />

              {currentUsers.length === 0 ? (
                <p className="empty-state-text">
                  {workFilter === "all"
                    ? "No students match your search criteria."
                    : `No students with ${workFilter === "homework" ? "homework" : "assessment"} results match.`}
                </p>
              ) : (
                currentUsers.map((user) => {
                  const userSubs = user.items;
                  const selectedAttempt = user.attempts?.find(({ id }) => id === user.selectedAttemptId) || null;
                  const selectedOutcome = selectedAttempt?.outcome?.status === "complete" ? selectedAttempt.outcome : null;
                  const assessmentSummary = user.finalResult || user.summary || selectedOutcome;
                  const progression = attemptProgression(user.attempts || []);
                  const userTotalMarks = userSubs.reduce(
                    (acc, s) => acc + (parseFloat(s.markAwarded) || 0),
                    0
                  );
                  const userTotalMaxMarks = userSubs.reduce(
                    (acc, s) => acc + (parseFloat(s.maxMark) || 0),
                    0
                  );
                  const userPercentage =
                    userTotalMaxMarks > 0
                      ? ((userTotalMarks / userTotalMaxMarks) * 100).toFixed(2)
                      : "N/A";
                  
                  // Add score color class based on percentage
                  let scoreColorClass = "score-neutral";
                  if (userPercentage !== "N/A") {
                    const percent = parseFloat(userPercentage);
                    if (percent >= 80) scoreColorClass = "score-excellent";
                    else if (percent >= 60) scoreColorClass = "score-good";
                    else if (percent >= 40) scoreColorClass = "score-average";
                    else scoreColorClass = "score-poor";
                  }
                  
                  const isExpanded = expandedStudents[user.userId] || false;
                  const awaitingResponseCount = userSubs.filter(submissionNeedsMarking).length;
                  const statusLabel = user.displayStatus === "reattempt_required"
                    ? "Reattempt required"
                    : user.attempts?.at(-1)?.status === "expired"
                    ? user.attempts?.at(-1)?.expiryReason === "assignment_deadline" ? "Closed" : "Time up"
                    : user.displayStatus === "in_progress"
                    ? "In progress"
                    : user.displayStatus === "marking" || awaitingResponseCount > 0
                      ? "Marking in progress"
                      : "Completed";
                  
                  return (
                    <div
                      key={user.userId}
                      className="student-card"
                    >
                      <div
                        className="student-header"
                        onClick={() => toggleStudentExpansion(user.userId)}
                        role="button"
                        tabIndex="0"
                        aria-expanded={isExpanded}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleStudentExpansion(user.userId);
                          }
                        }}
                      >
                        <div className="vqr-student-identity">
                          <span className={`student-toggle ${isExpanded ? 'expanded' : ''}`}>▶</span>
                          <span className="vqr-student-avatar" aria-hidden="true">{initialsForStudent(user.username)}</span>
                          <div>
                            <h3 className="student-name">{user.username}</h3>
                            <WorkTypeTag type={user.workType === undefined ? "assessment" : user.workType} className="vqr-work-type" />
                            <span>{userSubs.length} {userSubs.length === 1 ? "response" : "responses"}</span>
                          </div>
                        </div>
                        <div className="vqr-student-summary">
                          <span className={`vqr-status-badge ${statusLabel === "Completed" ? "is-marked" : statusLabel === "Reattempt required" ? "is-reattempt" : "is-awaiting"}`}>
                            {statusLabel}
                          </span>
                          <div className="vqr-result-summary">
                            <span className={`student-score ${scoreColorClass}`}>
                              <span className="score-label">Assessment Result:</span>{" "}
                              {Number.isFinite(assessmentSummary?.percentage) ? `${assessmentSummary.percentage.toFixed(2)}%` : `${userPercentage}%`}
                            </span>
                            {assessmentSummary?.achievedOutcomeLabel && (
                              <span className="vqr-outcome-chip">
                                {outcomeDisplayLabel(assessmentSummary.achievedOutcomeLabel)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="student-details">
                          <section className="vqr-result-context" aria-label={`Attempt history for ${user.username}`}>
                            <div className="vqr-canonical-result">
                              <span>Assessment Result</span>
                              <strong>{Number.isFinite(assessmentSummary?.percentage) ? `${assessmentSummary.percentage.toFixed(2)}%` : "Marking in progress"}</strong>
                              {assessmentSummary?.achievedOutcomeLabel && <em>{outcomeDisplayLabel(assessmentSummary.achievedOutcomeLabel)}</em>}
                              {Number.isFinite(assessmentSummary?.totalAwarded) && Number.isFinite(assessmentSummary?.totalPossible) && <small>{assessmentSummary.totalAwarded} / {assessmentSummary.totalPossible} marks</small>}
                              {user.finalResult && <small>Result method: {finalPolicyLabel(user.finalResult.policy)}</small>}
                              {progression && <small>Change from Attempt 1: {progression.label}</small>}
                              <button type="button" className="vqr-pdf-button"
                                disabled={Boolean(generatingPdfFor)} onClick={() => downloadFeedbackPdf(user)}>
                                <Download size={15} />
                                {generatingPdfFor === user.userId ? "Generating PDF..." : "Download Feedback PDF"}
                              </button>
                              {pdfError?.userId === user.userId && <span className="vqr-pdf-error" role="alert">{pdfError.message}</span>}
                            </div>
                            <div className="vqr-attempt-context">
                              <div className="vqr-attempt-heading">
                                <span>Attempt History</span>
                                <strong>Viewing {selectedAttempt ? attemptLabel(selectedAttempt.attemptNumber) : "attempt details"}</strong>
                              </div>
                              <div className="vqr-attempt-selector">
                                {(user.attempts || []).map((attempt) => {
                                  const complete = attempt.outcome?.status === "complete";
                                  const status = attempt.status === "expired"
                                    ? attempt.expiryReason === "assignment_deadline" ? "Closed" : "Time up"
                                    : attempt.status === "in_progress" ? "In progress" : complete ? "Completed" : "Marking in progress";
                                  return <button key={attempt.id} type="button"
                                    className={attempt.id === user.selectedAttemptId ? "selected" : ""}
                                    aria-pressed={attempt.id === user.selectedAttemptId}
                                    onClick={() => selectStudentAttempt(user.userId, attempt.id)}>
                                    <strong>{attemptLabel(attempt.attemptNumber)}</strong><span>{status}</span>
                                    {complete && <small>{attempt.outcome.totalAwarded} / {attempt.outcome.totalPossible} · {attempt.outcome.percentage.toFixed(2)}%{attempt.outcome.achievedOutcomeLabel ? ` · ${outcomeDisplayLabel(attempt.outcome.achievedOutcomeLabel)}` : ""}</small>}
                                    {user.finalResult?.selectedAttemptId === attempt.id && <small>Counts as assessment result</small>}
                                  </button>;
                                })}
                              </div>
                              <p className="vqr-viewing-result">
                                <b>{selectedAttempt ? `Viewing ${attemptLabel(selectedAttempt.attemptNumber)}:` : "Viewing attempt:"}</b>{" "}
                                {selectedAttempt?.status === "in_progress" ? "In progress" : selectedOutcome
                                  ? `${selectedOutcome.totalAwarded} / ${selectedOutcome.totalPossible} · ${selectedOutcome.percentage.toFixed(2)}%${selectedOutcome.achievedOutcomeLabel ? ` · ${outcomeDisplayLabel(selectedOutcome.achievedOutcomeLabel)}` : ""}`
                                  : "Marking in progress"}
                              </p>
                            </div>
                          </section>
                          <div className="student-responses-container">
                            {userSubs.length === 0 && <p className="empty-state-text">No completed responses are available for this attempt.</p>}
                            {userSubs.some((sub) => sub.questionOrderSource === "assessment") && (
                              <p className="vqr-order-note">
                                Question numbers follow the test's current order. This attempt was submitted before StudyDen recorded each attempt's own question order.
                              </p>
                            )}
                            {userSubs.map((sub, position) => (
                              <div key={sub.id} className="response-item">
                                <div 
                                  className="response-header"
                                  onClick={(e) => {
                                    // Prevent the click from triggering when clicking on the input or button
                                    if (
                                      e.target.tagName !== 'INPUT' && 
                                      e.target.tagName !== 'BUTTON' &&
                                      !e.target.closest('.mark-input-container') &&
                                      !e.target.closest('.save-button')
                                    ) {
                                      toggleQuestionExpansion(sub.id);
                                    }
                                  }}
                                >
                                  <div className="response-title-container">
                                    <span className={`question-toggle ${expandedQuestions[sub.id] ? 'expanded' : ''}`}>▶</span>
                                    <div>
                                      <div className="response-title">{questionNumberLabel(sub, position)}</div>
                                      <span className="vqr-response-time">Submitted {sub.submittedAt}</span>
                                    </div>
                                  </div>
                                  <div className="mark-controls">
                                    <div className="mark-input-container">
                                      <label className="mark-label" htmlFor={`mark-${sub.id}`}>Mark:</label>
                                      <input
                                        id={`mark-${sub.id}`}
                                        type="number"
                                        value={manualMarks[sub.id] || ""}
                                        onChange={(e) => handleMarkChange(sub.id, e.target.value)}
                                        className="mark-input"
                                        min="0"
                                        max={sub.maxMark}
                                      />
                                      <div className="max-mark-container">
                                        <span className="max-mark-slash">/</span>
                                        <span className="max-mark">{sub.maxMark}</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent triggering collapse
                                        saveManualMark(sub.id);
                                      }}
                                      className="save-button"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                                
                                {expandedQuestions[sub.id] && (
                                  <div className="response-content">
                                    <div className="response-row">
                                      <div className="response-cell question-cell">
                                        <div className="cell-label">Question:</div>
                                        <div className="truncated-text question-text">
                                          {sub.questionDisplay}
                                          {(sub.questionImageURL || sub.questionMedia) && (
                                            <div className="question-image-container">
                                              <QuestionImage question={sub} quizId={selectedQuizId}
                                                context={{ type: "owner" }} alt="Question"
                                                className="question-image" />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="response-cell answer-cell">
                                        <div className="cell-label">{sub.questionType === "multipleChoice" ? "Response review:" : "Student Answer:"}</div>
                                        {sub.questionType === "multipleChoice" && sub.mcqResult ? (
                                          <McqResultSummary
                                            mcqResult={sub.mcqResult}
                                            showCorrect
                                            studentLabel="Student answer"
                                          />
                                        ) : (
                                          <>
                                            <div className="truncated-text student-answer">{sub.userAnswer}</div>
                                            <SubmittedDrawingAnswer result={sub} questionText={sub.questionDisplay} typedAnswer={sub.userAnswer}
                                              mark={sub.markAwarded} maxMark={sub.maxMark} feedback={sub.feedback}
                                              questionLabel={questionNumberLabel(sub, position)} />
                                          </>
                                        )}
                                      </div>
                                    </div>
                                     
                                    <div className="response-row">
                                      {sub.questionType !== "multipleChoice" && (
                                        <div className="response-cell scheme-cell">
                                          <div className="cell-label">Mark Scheme:</div>
                                          <div className="truncated-text mark-scheme">
                                            {sub.markSchemeAnswer}
                                          </div>
                                        </div>
                                      )}
                                      <div className={`response-cell feedback-cell${sub.questionType === "multipleChoice" ? " mcq-feedback-cell" : ""}`}>
                                        <div className="cell-label">{sub.questionType === "multipleChoice" ? "Feedback:" : "AI Feedback:"}</div>
                                        <div className="truncated-text feedback-text">
                                          {typeof sub.feedback === "string" && sub.feedback.length > 0
                                            ? sub.feedback
                                            : "No feedback recorded."}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              
              {/* Pagination Controls */}
              {filteredAndSortedUsers.length > itemsPerPage && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-button"
                  >
                    Prev
                  </button>
                  
                  <span className="pagination-text">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="pagination-button"
                  >
                    Last
                  </button>
                </div>
              )}
            </section>
          )}

          {/* Empty State */}
          {!loading && !marking && assessmentSeries.length === 0 && !accessError && (
            <section className="vqr-card empty-state vqr-empty-state" aria-live="polite">
              <span className="vqr-empty-icon" aria-hidden="true">
                {selectedQuizId ? <UsersRound size={28} /> : <ClipboardCheck size={28} />}
              </span>
              <div>
                <h2>{selectedQuizId ? "No submissions yet" : "No assessment loaded"}</h2>
                <p className="empty-state-text">
                  {selectedQuizId
                    ? "Student responses will appear here once the assessment has been submitted."
                    : "Enter a test code above or choose from My Tests to review responses."}
                </p>
              </div>
            </section>
          )}
        </main>
      </div>

      {showTestChooser && <ChooseOwnedTestDialog userUid={user?.uid} onClose={() => setShowTestChooser(false)}
        onSelect={(code) => { setShowTestChooser(false); setQuizCode(code); loadQuizResponses(code); }} />}

      {showKeyCorrection && selectedQuizId && (
        <AnswerKeyCorrectionDialog
          quizId={selectedQuizId}
          studentNames={Object.fromEntries(submissions.map((item) => [item.userId, item.username]))}
          onClose={() => setShowKeyCorrection(false)}
          onApplied={() => reloadAuthoritativeResults().catch(() => {
            alert("The correction was applied, but the results could not be refreshed. Reload the page to see them.");
          })}
        />
      )}

      {/* ===== AI Marking Completion Modal ===== */}
      {showCompletionModal && (
        <div
          className="vqr-modal-overlay vqr-completion-overlay"
          onClick={(event) => {
            if (event.target.classList.contains("vqr-completion-overlay")) closeCompletionModal();
          }}
        >
          <section
            className={`vqr-modal vqr-completion-modal${completionSummary.hasFailures ? " has-attention" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="vqr-completion-title"
            aria-describedby="vqr-completion-description"
          >
            <span className="vqr-modal-corner vqr-modal-corner-top" aria-hidden="true" />
            <span className="vqr-modal-corner vqr-modal-corner-bottom" aria-hidden="true" />
            <button
              type="button"
              className="vqr-modal-close"
              onClick={closeCompletionModal}
              aria-label="Close marking completion"
            >
              <X size={18} />
            </button>

            <div className="vqr-completion-icon" aria-hidden="true">
              <CheckCircle2 size={30} />
            </div>
            <p className="vqr-modal-eyebrow">AI marking complete</p>
            <h2 id="vqr-completion-title">
              {completionSummary.hasFailures
                ? "Marking completed with items requiring attention"
                : "Assessment marked successfully"}
            </h2>
            <p id="vqr-completion-description" className="vqr-modal-description">
              {completionSummary.hasFailures
                ? "Available submissions were processed. Review the responses requiring attention below."
                : "All available submissions have been processed and the results are ready to review."}
            </p>

            <div className="vqr-completion-metrics" aria-label="Marking completion summary">
              <div><strong>{completionSummary.processed}</strong><span>Processed</span></div>
              <div><strong>{completionSummary.marked}</strong><span>Marked</span></div>
              <div><strong>{completionSummary.requiringAttention}</strong><span>Requiring attention</span></div>
            </div>

            <div className="vqr-modal-actions">
              <button
                ref={completionPrimaryRef}
                type="button"
                className="vqr-modal-primary"
                onClick={handleViewTopPerformers}
              >
                <Trophy size={17} />View Top Performers
              </button>
              <button type="button" className="vqr-modal-secondary" onClick={closeCompletionModal}>
                Review Results
              </button>
            </div>
          </section>
        </div>
      )}

      {/* ===== Top Performers Podium Modal ===== */}
      {showPodiumModal && podiumData.length > 0 && (
        <div
          className="podium-overlay vqr-modal-overlay vqr-podium-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("podium-overlay")) setShowPodiumModal(false);
          }}
        >
          <div
            className={`podium-modal vqr-modal vqr-podium-modal vqr-podium-layout-${Math.min(podiumData.length, 3)}`}
            role="dialog"
            aria-modal="true"
            aria-label="Top performers"
          >
            <span className="vqr-modal-corner vqr-modal-corner-top" aria-hidden="true" />
            <span className="vqr-modal-corner vqr-modal-corner-bottom" aria-hidden="true" />
            <button
              type="button"
              className="vqr-modal-close"
              onClick={() => setShowPodiumModal(false)}
              aria-label="Close top performers"
            >
              <X size={18} />
            </button>
            <div className="vqr-podium-header">
              <span className="vqr-podium-trophy" aria-hidden="true"><Trophy size={22} /></span>
              <div>
                <p className="vqr-modal-eyebrow">Top performers</p>
                <h2>Assessment Highlights</h2>
                <span>Celebrate the strongest results from this assessment.</span>
              </div>
            </div>

            <div className={`podium-stage vqr-performer-grid vqr-performer-count-${Math.min(podiumData.length, 3)}`}>
              {renderPodiumSpot(podiumData[1])}
              {renderPodiumSpot(podiumData[0])}
              {renderPodiumSpot(podiumData[2])}
            </div>

            {/* Buttons */}
            <div className="podium-actions">
              <button
                ref={podiumPrimaryRef}
                className="podium-btn podium-btn-results"
                onClick={() => setShowPodiumModal(false)}
              >
                View Full Results
              </button>
              <button
                className="podium-btn podium-btn-close"
                onClick={() => setShowPodiumModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewQuizResponses;
